import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Switch, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { v4 as uuidv4 } from 'uuid';
import MBOSWarningPopup from '@/components/MBOSWarningPopup/MBOSWarningPopup';
import SumsCriteriaPopup from '@/components/SumsCriteriaPopup/SumsCriteriaPopup';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import ButtonsContentTable from '@/components/UI/ButtonsContentTable/ButtonsContentTable';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import DialogDataSelection from '@/components/UI/DialogDataSelection/DialogDataSelection';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import FormControlLabel from '@/components/UI/FormControlLabel/FormControlLabel';
import InputText from '@/components/UI/InputText/InputText';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import DialogTableList from '@/components/UI/TableListEditable/DialogTableList/DialogTableList';
import MandatoryColumnTitle from '@/components/UI/TableListEditable/MandatoryColumnTitle/MandatoryColumnTitle';
import { CELL_EDITOR_TYPES, FILTER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import ValueByDIA4ULabel from '@/components/UI/TableListEditable/ValueByDIA4ULabel/ValueByDIA4ULabel';
import ValueByMBOSLabel from '@/components/UI/TableListEditable/ValueByMBOSLabel/ValueByMBOSLabel';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import { validateFlashingTable, validateCodingTable, validateBlockVehicleTable, validateSoftwareForm } from '@/helpers/campaignValidations';
import { tableDialogTitles, softwareTableDialogRows, YES, NO, CUSTOM_COLUMNS_TYPES, HELP_AREA, errorTypes, EMPTY_LANG_COMMENT, uuidCodeOld, SOFTWARE, TABLE_FIELD_MAX_LENGTH, TAB_FORMS_OPTIONS, getPartNumberEditingExtensions, ERROR } from '@/helpers/constants';
import {
  columnsSoftwareCodingParts, columnsExtensionSoftwareCodingParts, columnsSoftwareFlashingParts,
  columnsExtensionSoftwareFlashingParts, columnsSoftwareBlockVehicleParts,
  columnsExtensionSoftwareBlockVehicleParts,
  initDialogUpdateInitialData,
  processEditFieldSynchronization,
  hasDialogFieldsSynchronized,
  hasMBOSFieldsSynchronized,
} from '@/helpers/tables-utils';
import { isCampaignRDMP, isPDRSCreatedCampaign, isValidYWPFortmat, removeIdFromObject, isMbos as isMbosSelection, isFlashableRelease } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import useError from '@/hooks/useError';
import { usePdrsUnmark } from '@/hooks/usePdrsUnmark';
import useSave from '@/hooks/useSave';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignSoftware, setOriginalValues, setSoftWareValidations } from '@/redux/Campaign/campaign.action';
import {
  getCampaignSoftware, getCampaignHelp, getOriginalValues, getSoftwareValidations, getCampaignInfo,
  getCampaignValidationsActive, getIsVANCR,
} from '@/redux/Campaign/campaign.reducer';
import { setSoftwareForms } from '@/redux/Forms/forms.action';
import { getSoftwareForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import { AffectedSoftware, AffectedSoftwareOriginalValues, CampaignInfo, SBMStatusDto } from '@/types';
import type { ColumnConfig } from '@/types/TableListEditable';
import SfmNumberAccordion from './SfmNumberAccordion/SfmNumberAccordion';
import styles from './Software.module.scss';

interface PartNumberAssignment {
  partId?: string;
  section: string;
}

interface Assignments {
  parts: string[];
  campaignId: string;
}

interface TableValidationsObject {
  [rowIndex: number]: {
    [fieldName: string]: {
      isValid: boolean;
      error: string;
    };
  };
}

interface TableValidationInfo {
  invalid: boolean;
  validations: TableValidationsObject;
}

const useStyles = makeStyles(() => ({
  formControlLabel: { alignItems: 'unset', marginLeft: 0 },
  columCoding: { flexBasis: '25%' },
  columFlashing: { flexBasis: '25%' },
  columnBlockVehicle: { flexBasis: '50%' },
  columCodingTable: { flexBasis: '100%', overflow: 'auto' },
  drowpDownRemoteFlashing: { width: '100%%' },
}));

const setUuidsSoftware = (soft: AffectedSoftware) => ({
  ...soft,
  flashingInformation: {
    ...soft?.flashingInformation,
    sfm: soft?.flashingInformation?.sfm.map((sfm) => ({ ...sfm, uuid: uuidv4() })),
  },
});

const editingStateColumnExtensionsDoorman = [
  { columnName: 'doormanBundleId', editingEnabled: false },
  { columnName: 'sbmStatus', editingEnabled: false },
];

const DisabledSwitchTooltip = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

const [CODING, CODINGUUID, FLASHING, FLASHINGUUID, BLOCKVEHICLE, BLOCKVEHICLEUUID] = ['codingParts', 'codingUuids',
  'flashingParts', 'flashingUuids', 'doormanParts', 'doormanUuids'];

const SOFTWARE_TABLE_COLUMNS = {
  ID: 'id',
  NUMBER_NEW: 'numberNew',
  UUID_CODE: 'uuidCode',
  NUMBER_OLD: 'numberOld',
  UUID_CODE_OLD: 'uuidCodeOld',
  DESCRIPTION: 'description',
  ADDITIONAL_INFORMATION: 'additionalInformation',
  FREE_TEXT: 'freeText',
  ECU_IS_CODEABLE: 'ecuIsCodeable',
  DOORMAN_BUNDLE_ID: 'doormanBundleId',
  SBM_STATUS: 'sbmStatus',
  BTV_PERSON: 'btvPerson',
  UUID_PERSON_RESPONSIBLE: 'uuidPersonResponsible',
  YWP_VERSION_OLD: 'ywpVersionOld',
  YWP_VERSION_NEW: 'ywpVersionNew',
};

const wrapperSoftwareValidations = (table: string, software: AffectedSoftware): TableValidationInfo => {
  const tablesValidations = {
    [CODING]: validateCodingTable(software),
    [FLASHING]: validateFlashingTable(software),
    [BLOCKVEHICLE]: validateBlockVehicleTable(software),
  };

  return tablesValidations[table];
};

interface SoftwareProps {
  campaignSoftware: AffectedSoftware;
  setSoftware: (software: AffectedSoftware) => void;
  originalValues: AffectedSoftwareOriginalValues;
  setOriginValues: (p: { values: AffectedSoftware | {}, hasChange: boolean }) => void; // eslint-disable-line
  setHelpArea: (area: string) => void;
  subCR: boolean;
  softwareValidation: any;
  headerLanguage: string;
  forms: any;
  setForms: (forms: any) => void; // eslint-disable-line
  setValidations: (validations: any) => void; // eslint-disable-line
  validationsActive: boolean;
  campaignInfo: CampaignInfo;
  isVans: boolean;
}

const Software: React.FC<SoftwareProps> = (props) => {
  const { campaignSoftware, setSoftware, originalValues, setOriginValues, setHelpArea, subCR, softwareValidation,
    headerLanguage, forms, setForms, setValidations, validationsActive, campaignInfo, isVans } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const lang = useAppLanguage();
  const [tablesSelection, setTablesSelection] = useState<{ [key: string]: number[] }>({
    [CODING]: [],
    [CODINGUUID]: [],
    [FLASHING]: [],
    [FLASHINGUUID]: [],
    [BLOCKVEHICLE]: [],
    [BLOCKVEHICLEUUID]: [],
  });
  const [tableValidations, setTableValidations] = useState<{ [table: string]: TableValidationsObject | null }>({ [CODING]: null, [FLASHING]: null, [BLOCKVEHICLE]: null });
  const [isLoading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState('');
  const [openNewRowDialog, setOpenNewRowDialog] = useState('');
  const [sumsCriteriaDialog, setSumsCriteriaDialog] = useState(false);
  const [isMBOSWarningOpened, setIsMBOSWarningOpened] = useState(false);
  const [openPNList, setOpenPNList] = useState<Assignments | null>(null);
  const [partNumberAssignment, setPartNumberAssignment] = useState<PartNumberAssignment | null>(null);
  const [tableValue, setTableValue] = useState<any>({});
  const { id, version } = useParams<{ id: string; version: string }>();
  const classes = useStyles();
  const { detectLanguage, detectLanguageTranslation } = useDetectLanguage();
  const checkDisabled = useCheckDisabled(true) || subCR;
  const save = useSave('affected-software');
  const pendingSaveRef = useRef(false);
  const { handleCriticalError } = useError();
  const isDoormanProcess = campaignInfo?.doormanSelected;
  const isRDMP = isCampaignRDMP(campaignInfo?.creationUser?.department);
  const isPDRSCampaign = isPDRSCreatedCampaign(campaignInfo);
  const { unmarkPdrsIds } = usePdrsUnmark(campaignInfo?.id);
  const isMbos = isMbosSelection(campaignSoftware?.mbosDetails);
  const isFlashableSelected = isFlashableRelease(campaignSoftware?.mbosDetails);
  const isSelectedSoftwareOption: boolean = (campaignSoftware?.flashing || campaignSoftware?.coding);
  const buttonContentText = (isUUID: boolean) => (isUUID ? t('software.addUUID') : t('software.addPart'));

  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      save();
    }
  });

  const areMandatoryMbosOtaFieldsAnswered = useMemo(() => {
    const mbos = campaignSoftware?.mbosDetails;

    if (!mbos?.isMbos) return false;

    if (isMbos) {
      if (!mbos.releaseFlashable) return false;
      if (mbos.releaseFlashable === NO && !mbos.stillFlashable) return false;
    }

    if (!campaignSoftware?.otaUpdate) return false;

    if (campaignSoftware.otaUpdate === NO) {
      if (!campaignSoftware.eeCommissioningRequired) return false;
      if (isMbos && !campaignSoftware.otaComment?.[headerLanguage]) return false;
    }

    return true;
  }, [campaignSoftware?.mbosDetails, campaignSoftware?.otaUpdate, campaignSoftware?.eeCommissioningRequired, campaignSoftware?.otaComment, headerLanguage]); // eslint-disable-line

  const isCodingTableVisible = useMemo(
    () => campaignSoftware?.coding && areMandatoryMbosOtaFieldsAnswered,
    [campaignSoftware?.coding, areMandatoryMbosOtaFieldsAnswered],
  );

  const isFlashingInfoVisible = useMemo(
    () => campaignSoftware?.flashing && areMandatoryMbosOtaFieldsAnswered,
    [campaignSoftware?.flashing, areMandatoryMbosOtaFieldsAnswered],
  );

  const isFlashingTableVisible = useMemo(() => {
    if (!isFlashingInfoVisible) return false;

    const sfmRequested = campaignSoftware?.flashingInformation?.sfmRequested;

    if (sfmRequested === NO) return true;

    if (sfmRequested === YES) {
      return campaignSoftware?.flashingInformation?.sfm?.some(
        (sfm) => sfm.requestNumber && sfm.requestNumber.trim() !== '',
      ) ?? false;
    }

    return false;
  }, [isFlashingInfoVisible, campaignSoftware?.flashingInformation?.sfmRequested, campaignSoftware?.flashingInformation?.sfm]);

  const isDoormanTableVisible = useMemo(
    () => campaignSoftware?.doormanSelected && isDoormanProcess,
    [campaignSoftware?.doormanSelected, isDoormanProcess],
  );

  const isPartNumberTableHidden = useMemo(
    () => isMbos && isFlashableSelected,
    [isMbos, isFlashableSelected],
  );

  const dialogDataColumns = useMemo(() => [
    { name: 'partNumberOld', title: t('software.partNoOld'), wrapText: true },
    { name: 'type', title: t('software.type'), wrapText: true },
    { name: 'zgsOld', title: t('software.zgsold'), wrapText: true },
  ], [t]);
  const tableDialog = softwareTableDialogRows(campaignSoftware, isRDMP);

  useEffect(() => {
    setTableValue(tableDialog);
  }, [campaignSoftware]);// eslint-disable-line

  const mandatoryColumns = (section: string) => (columnName: string) => {
    let mandatoryFlashing: string[] = [];

    if (isSelectedSoftwareOption && campaignSoftware?.otaUpdate === YES) {
      mandatoryFlashing = ['partNoNewNumber', 'diogenesName', 'hwPartNo', ...[campaignSoftware.flashing ? 'partNoOld' : '', uuidCodeOld]];
    } else if (isSelectedSoftwareOption && campaignSoftware?.mbosDetails?.releaseFlashable === YES && isMbos) {
      mandatoryFlashing = ['partNoNewNumber', 'partNoOld', uuidCodeOld];
    } else if (isSelectedSoftwareOption && isMbos && campaignSoftware?.otaUpdate === NO) {
      mandatoryFlashing = ['partNoNewNumber', 'partNoOld', SOFTWARE.OTA_COMMENT];
    };

    const conditions = {
      [CODING]: mandatoryFlashing.includes(columnName),
      [FLASHING]: campaignSoftware?.flashing && (isMbos
        ? columnName === 'partNoOld'
        : campaignSoftware?.otaUpdate === YES && ['partNoOld', 'partNoNewNumber', 'diogenesName', 'hwPartNo'].includes(columnName)),
      [BLOCKVEHICLE]: campaignSoftware?.doormanSelected,
      [CODINGUUID]: mandatoryFlashing?.includes(columnName),
    };

    const label = t(`software.${columnName}`);

    return conditions[section] ? `${label} *` : label;
  };

  const [tablesColumnExtensions, setTablesColumnExtensions] = useState({
    [CODING]: columnsExtensionSoftwareCodingParts(campaignSoftware, false, isRDMP),
    [CODINGUUID]: columnsExtensionSoftwareCodingParts(campaignSoftware, true, isRDMP),
    [FLASHING]: columnsExtensionSoftwareFlashingParts(campaignSoftware, false, isRDMP),
    [FLASHINGUUID]: columnsExtensionSoftwareFlashingParts(campaignSoftware, true, isRDMP),
    [BLOCKVEHICLE]: columnsExtensionSoftwareBlockVehicleParts(campaignSoftware, false),
    [BLOCKVEHICLEUUID]: columnsExtensionSoftwareBlockVehicleParts(campaignSoftware, true),
  });

  const columns = {
    [CODING]: columnsSoftwareCodingParts(campaignSoftware, isRDMP, false, mandatoryColumns(CODING), t),
    [CODINGUUID]: columnsSoftwareCodingParts(campaignSoftware, isRDMP, true, mandatoryColumns(CODINGUUID), t),
    [FLASHING]: columnsSoftwareFlashingParts(campaignSoftware, isRDMP, false, mandatoryColumns(FLASHING), t),
    [FLASHINGUUID]: columnsSoftwareFlashingParts(campaignSoftware, isRDMP, true, mandatoryColumns(FLASHINGUUID), t),
    [BLOCKVEHICLE]: columnsSoftwareBlockVehicleParts(campaignSoftware, false, mandatoryColumns(BLOCKVEHICLE), t),
    [BLOCKVEHICLEUUID]: columnsSoftwareBlockVehicleParts(campaignSoftware, true, mandatoryColumns(BLOCKVEHICLEUUID), t),
  };

  const softwareCodingPart = (coding) => coding?.map((result, index) => ({
    ...result,
    id: index,
    btvPerson: result.btvPerson && typeof result.btvPerson === 'object' ? result.btvPerson?.code : result.btvPerson,
    uuidPersonResponsible: result.uuidPersonResponsible && typeof result.uuidPersonResponsible === 'object' ? result.uuidPersonResponsible?.code : result.uuidPersonResponsible,
  }));

  const softwareFlashingPart = (flashing) => flashing.map((result, index) => ({
    ...result,
    id: index,
    btvPerson: result.btvPerson && typeof result.btvPerson === 'object' ? result.btvPerson?.code : result.btvPerson,
    uuidPersonResponsible: result.uuidPersonResponsible && typeof result.uuidPersonResponsible === 'object' ? result.uuidPersonResponsible?.code : result.uuidPersonResponsible,
  }));

  const softwareBlockVehiclePart = (blockVehicle) => blockVehicle.map((result, index) => ({
    ...result,
    id: index,
    sbmStatus: result.sbmStatus ? forms.sbmStatus?.options?.[result.sbmStatus]?.[lang] : result.sbmStatus,
  }));

  const wrapperSoftware = (table: string, content: any) => {
    if (table === CODING) return softwareCodingPart(content);
    if (table === CODINGUUID) return softwareCodingPart(content);
    if (table === FLASHING) return softwareFlashingPart(content);
    if (table === FLASHINGUUID) return softwareFlashingPart(content);
    if (table === BLOCKVEHICLE) return softwareBlockVehiclePart(content);

    return softwareBlockVehiclePart(content);
  };

  const onCopyOriginValues = (content: AffectedSoftware) => {
    const copyValues = {
      values: content,
      hasChange: false,
    };
    setOriginValues(copyValues);
  };

  const onSetOriginValues = () => {
    const values = {
      ...originalValues,
      hasChange: true,
    };
    setOriginValues(values);
  };

  const validateUpdateSoftwareTable = (label: string, software: AffectedSoftware) => {
    if (validationsActive) {
      const validations = wrapperSoftwareValidations(label, software);
      const newState = {
        ...softwareValidation,
        [headerLanguage]: {
          ...softwareValidation?.[headerLanguage],
          [label]: validations,
        },
      };

      if (software?.mbosDetails?.isMbos === YES) {
        const invalidPartNumberFound = Object.values(validations?.validations).some((validation) => !validation.numberNew?.isValid);

        if (invalidPartNumberFound) addToast(t('dialog.wrongPartNoFormat'), 'Warning');
      }

      setTableValidations({ ...tableValidations, [label]: validations?.validations });
      setValidations(newState);
    }
  };

  const hideColumns = (label: string, newState: AffectedSoftware) => {
    switch (label) {
      case SOFTWARE.OTA_UPDATE:
        if (newState[label] === YES) addToast(t('software.remoteUpdateAlert'), 'Warning', errorTypes.software_remote_update_alert);
        if (newState?.coding) {
          setTablesColumnExtensions({
            ...tablesColumnExtensions,
            [CODING]: columnsExtensionSoftwareCodingParts(newState, false, isRDMP),
            [CODINGUUID]: columnsExtensionSoftwareCodingParts(newState, true, isRDMP),
          });
          validateUpdateSoftwareTable(CODING, newState);
        } else {
          setTablesColumnExtensions({
            ...tablesColumnExtensions,
            [FLASHING]: columnsExtensionSoftwareFlashingParts(newState, false, isRDMP),
            [FLASHINGUUID]: columnsExtensionSoftwareFlashingParts(newState, true, isRDMP),
          });
          validateUpdateSoftwareTable(FLASHING, newState);
        }
        break;
      case SOFTWARE.RELEASE_FLASHABLE:
        if (newState?.mbosDetails?.releaseFlashable === YES) addToast(t('software.partNumberAffectedAlert'), 'Warning');
        break;
      default:
        break;
    }
  };

  const cleanSFMNumbers = () => {
    const sfmNumbers = campaignSoftware?.flashingInformation?.sfm?.filter((sfm) => sfm.requestNumber !== '');

    return {
      flashingInformation: {
        ...campaignSoftware?.flashingInformation,
        sfmRequested: sfmNumbers.length > 0 ? YES : NO,
        sfm: sfmNumbers,
      },
    };
  };

  const checkValidationState = (newState: AffectedSoftware) => {
    if (validationsActive) {
      setValidations({
        ...softwareValidation,
        [headerLanguage]: validateSoftwareForm(softwareValidation, { affectedSoftware: newState }, lang, isVans),
      });
    }
  };

  const updateState = (label: string, value: string | boolean) => {
    const emptyMBOSDetails = { isMbos: '', releaseFlashable: '', stillFlashable: '' };
    const mbosDetailsFields = Object.keys(emptyMBOSDetails);
    const emptyStateFields = { otaUpdate: '', otaComment: EMPTY_LANG_COMMENT, mbosDetails: emptyMBOSDetails };

    let newState: AffectedSoftware = {
      ...campaignSoftware,
    };

    if ([SOFTWARE.FLASHING, SOFTWARE.CODING].includes(label) && value) setSumsCriteriaDialog(true);
    if (label === SOFTWARE.FLASHING && !value) newState = { ...newState, ...cleanSFMNumbers(), ...emptyStateFields };
    if (label === SOFTWARE.CODING && !value) newState = { ...newState, ...emptyStateFields };
    if (label === SOFTWARE.OTA_UPDATE) newState = { ...newState, otaComment: EMPTY_LANG_COMMENT };

    if (label === SOFTWARE.SFM_REQUESTED) {
      newState = {
        ...newState,
        flashingInformation: {
          sfm: value === YES ? ([{ requestNumber: '', updateTemporaryInlineFlashing: false, uuid: uuidv4() }]) : [],
          sfmRequested: value as string,
        },
      };
    } else if (label === SOFTWARE.OTA_COMMENT) {
      newState = {
        ...newState,
        [label]: {
          ...newState[label],
          [headerLanguage]: value,
        },
      };
    } else if (mbosDetailsFields.includes(label)) {
      newState = {
        ...newState,
        mbosDetails: {
          ...newState?.mbosDetails,
          ...(label === SOFTWARE.IS_MBOS && value === NO) ? { releaseFlashable: '', stillFlashable: '' } : {},
          ...(label === SOFTWARE.RELEASE_FLASHABLE && value === YES) ? { stillFlashable: '' } : {},
          [label]: value as string,
        },
      };
    } else {
      newState = {
        ...newState,
        [label]: value,
      };
    }

    if (label === SOFTWARE.IS_MBOS) {
      validateUpdateSoftwareTable(newState?.coding ? CODING : FLASHING, newState);

      if (value === YES) setIsMBOSWarningOpened(true);
    }
    onSetOriginValues();
    setSoftware(newState);
    hideColumns(label, newState);
    checkValidationState(newState);
  };

  useEffect(() => {
    async function initSoftwareTab() {
      if (subCR) {
        Promise.all([
          campaignServices.getSubCampaign(id, 'affected-software', version),
          campaignServices.getTabForms(TAB_FORMS_OPTIONS.AFFECTED_SOFTWARE),
        ])
          .then((data) => {
            const [software, form] = data;
            let softwareWithUuids = setUuidsSoftware(software?.module);
            softwareWithUuids = initDialogUpdateInitialData(softwareWithUuids) as AffectedSoftware;
            onCopyOriginValues(softwareWithUuids);
            setSoftware(softwareWithUuids);
            setTablesColumnExtensions({
              ...tablesColumnExtensions,
              [CODING]: columnsExtensionSoftwareCodingParts(softwareWithUuids, false, isRDMP),
              [CODINGUUID]: columnsExtensionSoftwareCodingParts(softwareWithUuids, true, isRDMP),
              [FLASHING]: columnsExtensionSoftwareFlashingParts(softwareWithUuids, false, isRDMP),
              [FLASHINGUUID]: columnsExtensionSoftwareFlashingParts(softwareWithUuids, true, isRDMP),
            });
            setForms(form.form);
          })
          .catch(handleCriticalError)
          .finally(() => setLoading(false));
      } else {
        Promise.all([
          campaignServices.getCampaignRetry(id, 'affected-software', version),
          campaignServices.getTabForms(TAB_FORMS_OPTIONS.AFFECTED_SOFTWARE),
        ])
          .then((res) => {
            const [software, form] = res;
            let softwareWithUuids = setUuidsSoftware(software);
            softwareWithUuids = initDialogUpdateInitialData(softwareWithUuids) as AffectedSoftware;
            onCopyOriginValues(softwareWithUuids);
            setSoftware(isDoormanProcess ? { ...softwareWithUuids, doormanSelected: isDoormanProcess } : softwareWithUuids);
            setTablesColumnExtensions({
              ...tablesColumnExtensions,
              [CODING]: columnsExtensionSoftwareCodingParts(softwareWithUuids, false, isRDMP),
              [CODINGUUID]: columnsExtensionSoftwareCodingParts(softwareWithUuids, true, isRDMP),
              [FLASHING]: columnsExtensionSoftwareFlashingParts(softwareWithUuids, false, isRDMP),
              [FLASHINGUUID]: columnsExtensionSoftwareFlashingParts(softwareWithUuids, true, isRDMP),
            });
            setForms(form.form);
          })
          .catch(handleCriticalError)
          .finally(() => setLoading(false));
      }
    }
    initSoftwareTab();
  }, []);

  const getEditingStateColumnExtensions = (section: string, columns: any, index, hasPartNumberOld: boolean) => {
    if (index !== 0) return [];

    if ((isMbos && isFlashableSelected && hasPartNumberOld)
      || (section === CODINGUUID && isPartNumberTableHidden && !!campaignSoftware[section]?.some((row) => row.uuidCodeOld))) {
      return columns[section]?.map((col) => ({
        columnName: col.name,
        editingEnabled: false,
      })) || [];
    }

    return getPartNumberEditingExtensions(isPDRSCampaign);
  };

  const handleTableEdit = (newValue, editProps, type, table) => { // special columns, dropdowns or user field
    if (editProps?.row && editProps?.column) {
      let rowEdited = {
        ...editProps?.row,
        [editProps?.column?.name]: type === CUSTOM_COLUMNS_TYPES.UNIQUE_USER_SELECTION ? newValue : newValue?.target?.value,
      };
      rowEdited = processEditFieldSynchronization(rowEdited);

      const changedRows = wrapperSoftware(table, campaignSoftware[table])
        .map((current) => removeIdFromObject(current.id === rowEdited.id ? rowEdited : current));

      const newState = {
        ...campaignSoftware,
        [table]: changedRows,
      };
      onSetOriginValues();
      setSoftware(newState);
    }
  };

  const formatCellValue = (columnName: string, value: unknown) => {
    if (typeof value !== 'string') return value;

    const maxLength = TABLE_FIELD_MAX_LENGTH[columnName];
    let newValue = value;

    if ([SOFTWARE_TABLE_COLUMNS.YWP_VERSION_OLD, SOFTWARE_TABLE_COLUMNS.YWP_VERSION_NEW].includes(columnName)) {
      const digits = newValue.replace(/\D/g, '');
      const truncated = digits.slice(0, 6);
      newValue = truncated.match(/.{1,2}/g)?.join('/') || truncated;
    }

    if (maxLength && newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }

    return newValue;
  };

  const getSectionColumns = (section: string, index: number): ColumnConfig[] => {
    const baseColumns = columns[section] || [];
    const extensions = tablesColumnExtensions[section] || [];
    const extensionByName = Object.fromEntries(extensions.map((e) => [e.columnName, e]));
    let editingExtensions = getEditingStateColumnExtensions(section, columns, index, !!campaignSoftware[section]?.some((row) => row.numberOld));

    if (section === BLOCKVEHICLE || section === BLOCKVEHICLEUUID) {
      editingExtensions = [...editingExtensions, ...editingStateColumnExtensionsDoorman];
    }
    const disabledSet = new Set(editingExtensions.filter((extension) => !extension.editingEnabled).map((column) => column.columnName));
    const isEditableTable = !checkDisabled && canEditInTables;
    const userColumnName = (isRDMP && (section.includes('coding') || section.includes('flashing')))
      ? (index === 0 ? SOFTWARE_TABLE_COLUMNS.BTV_PERSON : SOFTWARE_TABLE_COLUMNS.UUID_PERSON_RESPONSIBLE)
      : null;
    const ecuOptions = forms?.ecuIsCodeable?.options ?? {};
    const fixedColumns = {
      [CODING]: [SOFTWARE_TABLE_COLUMNS.NUMBER_NEW, SOFTWARE_TABLE_COLUMNS.DESCRIPTION],
      [CODINGUUID]: [SOFTWARE_TABLE_COLUMNS.UUID_CODE, SOFTWARE_TABLE_COLUMNS.DESCRIPTION],
      [FLASHING]: [SOFTWARE_TABLE_COLUMNS.NUMBER_NEW, SOFTWARE_TABLE_COLUMNS.DESCRIPTION],
      [FLASHINGUUID]: [SOFTWARE_TABLE_COLUMNS.UUID_CODE, SOFTWARE_TABLE_COLUMNS.DESCRIPTION],
      [BLOCKVEHICLE]: [SOFTWARE_TABLE_COLUMNS.DOORMAN_BUNDLE_ID, SOFTWARE_TABLE_COLUMNS.SBM_STATUS],
      [BLOCKVEHICLEUUID]: [SOFTWARE_TABLE_COLUMNS.DOORMAN_BUNDLE_ID, SOFTWARE_TABLE_COLUMNS.SBM_STATUS],
    }[section] || [];

    return baseColumns.map(({ tiltle, title, ...col }) => {
      const ext = extensionByName[col.name] || {};
      const rawTitle = (title ?? tiltle ?? '') as string;
      const isMandatoryColumn = rawTitle.endsWith(' *');
      const displayTitle = isMandatoryColumn ? rawTitle.slice(0, -2) : rawTitle;
      const config: ColumnConfig = {
        ...col,
        title: displayTitle,
        flex: ext.width ? ext.width / 200 : 1,
        width: ext.width,
        wrapText: ext.wordWrapEnabled,
        editable: isEditableTable && !disabledSet.has(col.name),
        ...(fixedColumns.includes(col.name) ? { pinned: 'left' as const } : {}),
        ...(isMandatoryColumn
          ? {
            headerComponent: MandatoryColumnTitle as any,
            headerComponentParams: { title: displayTitle, mandatoryLabelMargin: 8 },
          }
          : {}),
      };

      if ([SOFTWARE_TABLE_COLUMNS.ADDITIONAL_INFORMATION, SOFTWARE_TABLE_COLUMNS.DESCRIPTION, SOFTWARE_TABLE_COLUMNS.FREE_TEXT].includes(col.name)) {
        config.cellEditor = CELL_EDITOR_TYPES.LARGE_TEXT;
        config.cellEditorParams = {
          maxLength: TABLE_FIELD_MAX_LENGTH[col.name] ?? 512,
        } as any;
      } else if (col.name !== SOFTWARE_TABLE_COLUMNS.ID && !disabledSet.has(col.name) && isEditableTable) {
        config.inputText = { maxLength: TABLE_FIELD_MAX_LENGTH[col.name] ?? 32 };
      }

      if (col.name === SOFTWARE_TABLE_COLUMNS.ECU_IS_CODEABLE) {
        config.cellEditor = CELL_EDITOR_TYPES.SELECT;
        config.cellEditorParams = {
          values: Object.keys(ecuOptions),
          formatValue: (value: string) => ecuOptions[value]?.[lang] || value,
        } as any;
        config.valueFormatter = ({ value }: { value: string }) => ecuOptions[value]?.[lang] || value;
        config.filterType = FILTER_TYPES.SET;
      }

      if (col.name === userColumnName) {
        config.usersSelection = {
          popupTitle: t(`dialog.${col.name}`),
          isOnlyOneUserSelection: true,
          onlyOneUserIsObject: true,
          onChange: ({ rowData, columnName, value }) => handleTableEdit(value, { row: rowData, column: { name: columnName } }, CUSTOM_COLUMNS_TYPES.UNIQUE_USER_SELECTION, section),
        };
      }

      return config;
    });
  };

  const handleCellValueChanged = (section: string) => (event: any) => {
    if (!event.data || !event.colDef.field) return;
    const rowId = event.data[SOFTWARE_TABLE_COLUMNS.ID];
    const { field } = event.colDef;
    const newValue = formatCellValue(field, event.newValue);
    handleEditSoftwareTables({ changed: { [rowId]: { [field]: newValue } } }, section);
  };

  const handleDeleteRow = async (table: string) => {
    const rowsIdx = tablesSelection?.[table];
    const updatedRows = campaignSoftware?.[table]?.filter((tableRow, idx) => !rowsIdx.includes(idx));
    const newState = {
      ...campaignSoftware,
      [table]: updatedRows,
    };

    if (isPDRSCampaign) {
      const partNumbersSuccessfullyUnmarked = await unmarkPdrsIds(campaignSoftware?.[table], rowsIdx, () => setConfirmDialog(''));
      if (!partNumbersSuccessfullyUnmarked) return;
    }

    onSetOriginValues();
    setSoftware(newState);
    setTablesSelection({ ...tablesSelection, [table]: [] });
    pendingSaveRef.current = true;
  };

  const handleEditSoftwareTables = ({ changed }, table: string) => {
    const invalidEntries = Object.values(changed as Record<string, any>).filter((entry: any) => {
      const ywpVersionOld = entry?.[SOFTWARE_TABLE_COLUMNS.YWP_VERSION_OLD] || '';
      const ywpVersionNew = entry?.[SOFTWARE_TABLE_COLUMNS.YWP_VERSION_NEW] || '';

      return (
        (ywpVersionOld && !isValidYWPFortmat(ywpVersionOld))
        || (ywpVersionNew && !isValidYWPFortmat(ywpVersionNew))
      );
    });

    if (invalidEntries.length > 0) {
      addToast(t('dialog.YWPVersionFormat'), 'Warning', errorTypes.ywp_version_format_error);
    };

    const changedData = changed[Object.keys(changed)[0]];

    if (changedData !== undefined) {
      const changedRows = campaignSoftware?.[table]
        .map((row, index) => (changed[index] ? processEditFieldSynchronization({ ...row, ...changed[index] }) : row));
      const newState = {
        ...campaignSoftware,
        [table]: changedRows,
      };
      onSetOriginValues();
      validateUpdateSoftwareTable(table, newState);
      setSoftware(newState);
    }
  };

  const addNewRow = async (data, table: string) => {
    if (data[0]?.doormanBundleId && data[0]?.doormanBundleId !== '') {
      await campaignServices.getSbmStatus(data[0].doormanBundleId)
        .then((res: SBMStatusDto) => {
          data[0].sbmStatus = res.sbmStatus;
        })
        .catch(() => null);
    }
    const rows = campaignSoftware && [...campaignSoftware[table]];

    const newState = {
      ...campaignSoftware,
      [table]: [...rows, ...data],
    };

    if (isPDRSCampaign) {
      campaignServices.markAssignments(campaignInfo.id, [data[0].numberOld]).catch((error) => {
        addToast(error.message, ERROR);
      });
    }

    onSetOriginValues();
    setSoftware(newState);
    setOpenNewRowDialog('');
    pendingSaveRef.current = true;
  };

  const onDialogConfim = async () => {
    await handleDeleteRow(confirmDialog);
    setConfirmDialog('');
  };

  const handleCopyInTable = (field) => {
    const rowsCopied = tablesSelection?.[field].map((elem) => campaignSoftware?.[field].find((a, index) => index === elem)) || [];
    const rows = [...(campaignSoftware?.[field] || [])];
    rowsCopied.forEach((row) => rows.push(row));
    const newState = {
      ...campaignSoftware,
      [field]: rows,
    };
    onSetOriginValues();
    setSoftware(newState);
    setTablesSelection({ ...tablesSelection, [field]: [] });
  };

  const handleChange = ({ target }) => updateState(target.name, target.checked);

  const checkTopSwitchDisabled = (label) => (label === SOFTWARE.CODING ? campaignSoftware?.flashing && !campaignSoftware?.coding : campaignSoftware?.coding && !campaignSoftware?.flashing);

  const onUpdateSfmNumbers = (action, sfmId = 9999, field = '', value = '') => {
    onSetOriginValues();
    let flashingInformation = { ...campaignSoftware?.flashingInformation };

    if (action === 'delete') {
      flashingInformation = { ...flashingInformation, sfm: flashingInformation.sfm.filter((sfmNumber, index) => index !== sfmId) };
    } else if (action === 'add') {
      flashingInformation = { ...flashingInformation, sfm: [...flashingInformation.sfm, { requestNumber: '', updateTemporaryInlineFlashing: false, uuid: uuidv4() }] };
    } else {
      flashingInformation = {
        ...flashingInformation,
        sfm: flashingInformation.sfm.map((sfmNumber, index) => (index === sfmId
          ? {
            ...sfmNumber,
            [field]: value,
          }
          : sfmNumber
        )),
      };
    }

    const newState: AffectedSoftware = {
      ...campaignSoftware,
      flashingInformation,
    };
    setSoftware(newState);
    checkValidationState(newState);
  };

  const otaCommentNeeded = campaignSoftware?.mbosDetails?.isMbos === YES && campaignSoftware?.otaUpdate === NO;
  const isEEDefinitionCommissioning = campaignSoftware?.otaUpdate === NO;

  const onBlur = (name, value) => {
    detectLanguage(value);

    if (originalValues?.hasChange) {
      detectLanguageTranslation(name);
    }
  };

  const getAssignmentList = () =>
    campaignServices.getAssignments(campaignInfo.id)
      .catch(handleCriticalError);

  const handleNewRowPart = (section: string) => {
    if (isPDRSCampaign) {
      setPartNumberAssignment({ section: section });
      getAssignmentList().then((assignments) => {
        setOpenPNList(assignments);
      });
    } else {
      setOpenNewRowDialog(section);
    }
  };

  const saveDialogDataInfo = (dataSelected) => {
    setPartNumberAssignment({ partId: dataSelected[0].partNumberOld, section: partNumberAssignment?.section ?? '' });

    if (partNumberAssignment?.section) {
      const selectedPartNumber = dataSelected[0].partNumberOld;
      const updatedRows = [...softwareTableDialogRows(campaignSoftware, isRDMP, isPDRSCampaign ? '' : selectedPartNumber)[partNumberAssignment.section]];

      if (updatedRows.length > 0) {
        if (isPDRSCampaign) {
          updatedRows[0].numberOld = selectedPartNumber;
        } else {
          updatedRows[0].numberNew = selectedPartNumber;
        }
        updatedRows[0].zgsOld = dataSelected[0].zgsOld;
      }

      setTableValue({
        ...tableValue,
        [partNumberAssignment.section]: updatedRows,
      });

      setOpenNewRowDialog(partNumberAssignment.section);
      setOpenPNList(null);
    };
  };

  const displaySyncFieldsLegend = (softwareSection: string): JSX.Element => {
    if (hasDialogFieldsSynchronized(softwareSection)) return <ValueByDIA4ULabel isTableLegend />;
    if (hasMBOSFieldsSynchronized(softwareSection)) return <ValueByMBOSLabel isTableLegend />;

    return <></>;
  };

  const canEditInTables = useMemo(() => {
    if (!campaignSoftware) return false;
    if (isDoormanProcess) return true;

    const { coding, flashing, mbosDetails, otaUpdate } = campaignSoftware;

    return ((coding || flashing) && mbosDetails?.isMbos !== '' && (mbosDetails.isMbos !== NO ? mbosDetails.releaseFlashable !== '' : true) && otaUpdate !== '');
  }, [campaignSoftware, isDoormanProcess]);

  // Remove when tables logic is completely implemented, currently tables are always enabled and the message is not needed
  // const CantEditTableMessage = !canEditInTables && (<InfoMsg msg={t('software.completeBeforeTables')} color="Yellow" />);

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <div className={styles.Software}>
            <AccordionPanel title={t('software.requirements')} handleClick={() => setHelpArea(HELP_AREA.REQUIREMENTS)}>
              <div className={classes.columCoding}>
                <div className={styles.Tooltip}>
                  <DisabledSwitchTooltip
                    condition={checkTopSwitchDisabled(SOFTWARE.CODING)}
                    wrapper={(children) => (<TooltipComponent title={t('software.flashingCodingSwitchTooltipMessage')}>{children}</TooltipComponent>)}
                  >
                    <div>
                      <FormControlLabel
                        disabled={checkDisabled || checkTopSwitchDisabled(SOFTWARE.CODING)}
                        fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, SOFTWARE.CODING]}
                        className={classes.formControlLabel}
                        label={t('software.coding')}
                        labelPlacement="top"
                        control={(

                          <Switch
                            disabled={checkDisabled || checkTopSwitchDisabled(SOFTWARE.CODING)}
                            checked={campaignSoftware?.coding}
                            onChange={handleChange}
                            name="coding"
                            color="primary"
                          />

                        )}
                      />
                    </div>
                  </DisabledSwitchTooltip>
                </div>
              </div>
              <div className={classes.columFlashing}>
                <div className={styles.Tooltip}>
                  <DisabledSwitchTooltip
                    condition={checkTopSwitchDisabled(SOFTWARE.FLASHING)}
                    wrapper={(children) => (<TooltipComponent title={t('software.flashingCodingSwitchTooltipMessage')}>{children}</TooltipComponent>)}
                  >
                    <div>
                      <FormControlLabel
                        disabled={checkDisabled || checkTopSwitchDisabled(SOFTWARE.FLASHING)}
                        className={classes.formControlLabel}
                        label={t('software.flashing')}
                        fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, SOFTWARE.FLASHING]}
                        labelPlacement="top"
                        control={(
                          <Switch
                            disabled={checkDisabled || checkTopSwitchDisabled(SOFTWARE.FLASHING)}
                            checked={campaignSoftware?.flashing}
                            onChange={handleChange}
                            name="flashing"
                            color="primary"
                          />
                        )}
                      />
                    </div>
                  </DisabledSwitchTooltip>
                </div>
              </div>
              {isDoormanProcess && (
                <div className={classes.columnBlockVehicle}>
                  <FormControlLabel
                    disabled={checkDisabled}
                    className={classes.formControlLabel}
                    label={<Typography>{t('software.blockVehicle')}</Typography>}
                    labelPlacement="top"
                    fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'doormanSelected']}
                    control={(
                      <Switch
                        disabled
                        checked
                        name="doormanSelected"
                        color="primary"
                      />
                    )}
                  />
                </div>
              )}
            </AccordionPanel>

            {(campaignSoftware?.coding || campaignSoftware?.flashing) && (
              <>
                <AccordionPanel
                  title={t('software.MBOSOtaRevelancy')}
                  handleClick={() => setHelpArea(campaignSoftware?.coding ? 'CODING_MBOS' : 'FLASHING_MBOS')}
                >
                  <div className={styles.ThreeColumns}>
                    <Dropdown
                      disabled={checkDisabled}
                      fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'mbosDetails', SOFTWARE.IS_MBOS]}
                      isMandatory
                      id="remote-software-isMbos"
                      label={t('software.isMbos')}
                      options={forms?.isMbos?.options ?? {}}
                      value={campaignSoftware?.mbosDetails?.isMbos}
                      handleChange={(ev) => updateState(SOFTWARE.IS_MBOS, ev.target.value)}
                      language={headerLanguage}
                      formControlClass={classes.drowpDownRemoteFlashing}
                    />
                  </div>
                  {isMbos && (
                    <div className={styles.ThreeColumns}>
                      <Dropdown
                        disabled={checkDisabled}
                        fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'mbosDetails', SOFTWARE.RELEASE_FLASHABLE]}
                        isMandatory
                        id="remote-software-releaseFlashable"
                        label={t('software.releaseFlashable')}
                        options={forms?.releaseFlashable?.options ?? {}}
                        value={campaignSoftware?.mbosDetails?.releaseFlashable}
                        handleChange={(ev) => updateState(SOFTWARE.RELEASE_FLASHABLE, ev.target.value)}
                        language={headerLanguage}
                        formControlClass={classes.drowpDownRemoteFlashing}
                      />
                    </div>
                  )}
                  {campaignSoftware?.mbosDetails?.releaseFlashable === NO && (
                    <div className={styles.ThreeColumns}>
                      <Dropdown
                        disabled={checkDisabled}
                        fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'mbosDetails', 'stillFlashable']}
                        isMandatory
                        id="remote-software-stillFlashable"
                        label={t('software.stillFlashable')}
                        options={forms?.stillFlashable?.options ?? {}}
                        value={campaignSoftware?.mbosDetails?.stillFlashable}
                        handleChange={(ev) => updateState('stillFlashable', ev.target.value)}
                        language={headerLanguage}
                        formControlClass={classes.drowpDownRemoteFlashing}
                      />
                    </div>
                  )}
                </AccordionPanel>

                <AccordionPanel
                  title={t('software.OTAUpdate')}
                  handleClick={() => setHelpArea(HELP_AREA.FLASHING_REMOTE_SOFTWARE_UPDATE)} // also CODING_REMOTE_SOFTWARE_UPDATE
                >
                  <div className={styles.ThreeColumns}>
                    <Dropdown
                      disabled={checkDisabled}
                      fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, SOFTWARE.OTA_UPDATE]}
                      isMandatory
                      id="remote-software-otaUpdate"
                      label={t('software.OTAUpdateField')}
                      options={forms?.otaUpdate?.options ?? {}}
                      value={campaignSoftware?.otaUpdate}
                      handleChange={(ev) => updateState(SOFTWARE.OTA_UPDATE, ev.target.value)}
                      language={headerLanguage}
                      formControlClass={classes.drowpDownRemoteFlashing}
                    />
                  </div>
                  {isEEDefinitionCommissioning && (
                    <div className={styles.TwoColumns}>
                      <Dropdown
                        disabled={checkDisabled}
                        fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'eeCommissioningRequired']}
                        isMandatory
                        label={t('software.eeCommissioningRequired')}
                        id="remote-software-eeCommissioningRequired"
                        options={forms?.eeCommissioningRequired?.options ?? {}}
                        value={campaignSoftware?.eeCommissioningRequired}
                        handleChange={(ev) => updateState('eeCommissioningRequired', ev.target.value)}
                        language={headerLanguage}
                        formControlClass={classes.drowpDownRemoteFlashing}
                      />
                    </div>
                  )}
                  {otaCommentNeeded && (
                    <div className={styles.TwoColumns}>
                      <InputText
                        disabled={checkDisabled}
                        fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, SOFTWARE.OTA_COMMENT]}
                        multiline
                        isMandatory
                        rows={4}
                        maxLength={256}
                        id="ota-comment"
                        label={t('software.otaComment')}
                        handleChange={(ev) => {
                          updateState(SOFTWARE.OTA_COMMENT, ev.target.value);
                        }}
                        value={campaignSoftware?.otaComment?.[headerLanguage] || ''}
                        onBlur={(ev) => onBlur(SOFTWARE.OTA_COMMENT, ev.target.value)}
                        helperText={[
                          ...((!checkDisabled && softwareValidation?.[headerLanguage]?.otaComment?.invalid)
                            ? [t(softwareValidation?.[headerLanguage]?.otaComment?.validations?.otaComment?.error, { maxLength: 256 })]
                            : []),
                          ...((!checkDisabled && !softwareValidation?.[headerLanguage]?.otaComment?.invalid
                            && campaignSoftware?.otaComment?.[headerLanguage]?.length === 256)
                            ? [t('validations.maxLength', { maxLength: 256 })]
                            : []),
                        ]}
                      />
                    </div>
                  )}
                </AccordionPanel>
              </>
            )}

            {isCodingTableVisible && (
              <AccordionPanel
                title={t('software.reworkCodingECUs')}
                handleClick={() => setHelpArea(HELP_AREA.WITH_CODING)}
                invalid={softwareValidation?.[headerLanguage]?.[CODING]?.invalid}
                fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'codingParts']}
              >
                <div className={classes.columCodingTable}>
                  {(isPartNumberTableHidden ? [CODINGUUID] : [CODING, CODINGUUID]).map((section, index) => (
                    <div key={`${section}-key`}>
                      <ButtonsContentTable
                        onOpenAddPopup={() => handleNewRowPart(section)}
                        onCopyRow={() => handleCopyInTable(section)}
                        onDeleteRow={() => setConfirmDialog(section)}
                        selection={tablesSelection?.[section]}
                        checkDisabled={checkDisabled || (index === 1 && isPDRSCampaign) || !canEditInTables}
                        label={t(`software.${(index === 0 && !isPartNumberTableHidden) || !isPartNumberTableHidden ? 'byPartNumber' : 'byUUID'}`)}
                        buttonText={isPDRSCampaign ? t('software.importPDRS') : buttonContentText(index === 1)}
                      />
                      {/* {CantEditTableMessage} */}
                      <TableListEditableNew
                        hasCheckbox={!checkDisabled}
                        list={wrapperSoftware(section, campaignSoftware[section]) as any}
                        onCellValueChanged={handleCellValueChanged(section)}
                        columns={getSectionColumns(section, index)}
                        selection={tablesSelection?.[section]}
                        onSelectionChange={(selection) => setTablesSelection({ ...tablesSelection, [section]: selection as number[] })}
                        validationStatus={tableValidations?.[section] || softwareValidation?.[headerLanguage]?.[section]?.validations}
                        hiddenColumns={[SOFTWARE_TABLE_COLUMNS.ID]}
                        filteringStateColumns={[{ columnName: SOFTWARE_TABLE_COLUMNS.ECU_IS_CODEABLE, filteringEnabled: false }]}
                        columnId={SOFTWARE_TABLE_COLUMNS.ID}
                      />
                      {openNewRowDialog === section && (
                        <DialogTableList
                          title="software.remoteSoftwareCoding"
                          table={`software-${section}`}
                          tableDialogRows={tableValue[section]}
                          tableDialogTitles={tableDialogTitles}
                          close={() => setOpenNewRowDialog('')}
                          onSave={(data) => addNewRow(data, section)}
                        />
                      )}
                      {(index === 0 && displaySyncFieldsLegend(campaignSoftware[section]))}
                    </div>
                  ))}
                </div>
              </AccordionPanel>
            )}
            {isFlashingInfoVisible && (
              <AccordionPanel title={t('software.flashingInformation')} handleClick={() => setHelpArea(HELP_AREA.FLASHING_INFORMATION)}>
                <div className={styles.FlexColumn} style={{ flexDirection: 'row' }}>
                  <div className={styles.ThreeColumns}>
                    <Dropdown
                      disabled={checkDisabled}
                      isMandatory
                      id="remote-software-coding-part"
                      label={t('software.sfm')}
                      options={forms?.sfmRequested?.options ?? {}}
                      value={campaignSoftware?.flashingInformation?.sfmRequested}
                      handleChange={(ev) => updateState(SOFTWARE.SFM_REQUESTED, ev.target.value)}
                      language={headerLanguage}
                      formControlClass={classes.drowpDownRemoteFlashing}
                      fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'flashingInformation', SOFTWARE.SFM_REQUESTED]}
                    />
                  </div>
                  {campaignSoftware?.flashingInformation?.sfmRequested === YES && !checkDisabled && (
                    <div className={styles.ThreeColumns}>
                      <div className={styles.LinkMail}>
                        <a href="http://sstrb195.edc.corpintra.net:8090/IDS/Antrag" target="_blank" rel="noreferrer">
                          {t('software.linkSFM')}
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {campaignSoftware?.flashingInformation?.sfmRequested === YES && (
                  <div className={styles.FlexColumn}>
                    <div className={styles.TabContainer}>
                      <div className={styles.CodeRuleHeader}>
                        <div className={styles.HeaderTitle}>
                          <strong>{t('software.requestNumbers')}</strong>
                        </div>
                        <div>
                          <SimpleButton
                            disabled={checkDisabled}
                            id="add-rule-button"
                            size="small"
                            color="primary"
                            buttonText={t('software.addNumber')}
                            handleClick={() => onUpdateSfmNumbers('add')}
                          />
                        </div>
                      </div>
                      {campaignSoftware?.flashingInformation?.sfm?.map((sfmNumber, index) => (
                        <SfmNumberAccordion
                          key={sfmNumber?.uuid}
                          id={index}
                          sfmNumber={sfmNumber}
                          onChange={onUpdateSfmNumbers}
                          canBeDeleted={!(index === 0 && campaignSoftware?.flashingInformation?.sfm.length === 1)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </AccordionPanel>
            )}
            {isFlashingTableVisible && (
              <AccordionPanel
                title={t('software.reworkFlashingECUs')}
                handleClick={() => setHelpArea(HELP_AREA.WITH_FLASHING)}
                invalid={softwareValidation?.[headerLanguage]?.[FLASHING]?.invalid}
                fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'flashingParts']}
              >
                <div className={classes.columCodingTable}>
                  {[FLASHING, FLASHINGUUID].map((section, index) => (
                    <div key={`${section}-key`}>
                      <ButtonsContentTable
                        onOpenAddPopup={() => handleNewRowPart(section)}
                        onCopyRow={() => handleCopyInTable(section)}
                        onDeleteRow={() => setConfirmDialog(section)}
                        selection={tablesSelection?.[section]}
                        checkDisabled={checkDisabled || !canEditInTables}
                        label={t(`software.${index === 0 ? 'byPartNumber' : 'byUUID'}`)}
                        buttonText={isPDRSCampaign ? t('software.importPDRS') : buttonContentText(index === 1)}
                      />
                      {/* {CantEditTableMessage} */}
                      <TableListEditableNew
                        hasCheckbox={!checkDisabled}
                        list={wrapperSoftware(section, campaignSoftware[section]) as any}
                        onCellValueChanged={handleCellValueChanged(section)}
                        columns={getSectionColumns(section, index)}
                        selection={tablesSelection?.[section]}
                        onSelectionChange={(selection) => setTablesSelection({ ...tablesSelection, [section]: selection as number[] })}
                        validationStatus={tableValidations?.[section] || softwareValidation?.[headerLanguage]?.[section]?.validations}
                        hiddenColumns={[SOFTWARE_TABLE_COLUMNS.ID]}
                        filteringStateColumns={[{ columnName: SOFTWARE_TABLE_COLUMNS.ECU_IS_CODEABLE, filteringEnabled: false }]}
                        columnId={SOFTWARE_TABLE_COLUMNS.ID}
                      />
                      {openNewRowDialog === section && (
                        <DialogTableList
                          title="software.flashing"
                          table={`software-${section}`}
                          tableDialogRows={tableValue[section]}
                          tableDialogTitles={tableDialogTitles}
                          close={() => setOpenNewRowDialog('')}
                          onSave={(data) => addNewRow(data, section)}
                        />
                      )}
                      {(index === 0 && displaySyncFieldsLegend(campaignSoftware[section]))}
                    </div>
                  ))}
                </div>
              </AccordionPanel>
            )}
            {isDoormanTableVisible && (
              <AccordionPanel
                title={t('software.dataForDoorman')}
                // Don't forget create new help area in admin when it will be defined by business
                handleClick={() => setHelpArea(HELP_AREA.WITH_CODING)}
                invalid={softwareValidation?.[headerLanguage]?.[BLOCKVEHICLE]?.invalid}
                fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'doormanParts']}
              >
                <div className={classes.columCodingTable}>
                  {[BLOCKVEHICLE, BLOCKVEHICLEUUID].map((section, index) => (
                    <div key={`${section}-key`}>
                      <ButtonsContentTable
                        onOpenAddPopup={() => handleNewRowPart(section)}
                        onCopyRow={() => handleCopyInTable(section)}
                        onDeleteRow={() => setConfirmDialog(section)}
                        selection={tablesSelection?.[section]}
                        checkDisabled={checkDisabled || (index === 1 && isPDRSCampaign) || !canEditInTables}
                        label={t(`software.${index === 0 ? 'byPartNumber' : 'byUUID'}`)}
                        buttonText={isPDRSCampaign ? t('software.importPDRS') : buttonContentText(index === 1)}
                      />
                      {/* {CantEditTableMessage} */}
                      <TableListEditableNew
                        hasCheckbox={!checkDisabled}
                        list={wrapperSoftware(section, campaignSoftware[section]) as any}
                        onCellValueChanged={handleCellValueChanged(section)}
                        columns={getSectionColumns(section, index)}
                        selection={tablesSelection?.[section]}
                        onSelectionChange={(selection) => setTablesSelection({ ...tablesSelection, [section]: selection as number[] })}
                        validationStatus={tableValidations?.[section] || softwareValidation?.[headerLanguage]?.[section]?.validations}
                        hiddenColumns={[SOFTWARE_TABLE_COLUMNS.ID]}
                        columnId={SOFTWARE_TABLE_COLUMNS.ID}
                      />
                      {openNewRowDialog === section && (
                        <DialogTableList
                          title="software.dataForDoorman"
                          table={`software-${section}`}
                          tableDialogRows={tableValue[section]}
                          tableDialogTitles={tableDialogTitles}
                          close={() => setOpenNewRowDialog('')}
                          onSave={(data) => addNewRow(data, section)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </AccordionPanel>
            )}
            {openPNList && openPNList?.parts && (
              <DialogDataSelection
                title={t('dialog.partNumberAssignment')}
                dataToSelect={openPNList?.parts}
                columns={dialogDataColumns}
                columnSort="codeRule"
                onSave={saveDialogDataInfo}
                onClose={() => {
                  setOpenPNList(null);
                  setPartNumberAssignment(null);
                  setTableValue(tableDialog);
                }}
              />
            )}
            {confirmDialog && (
              <ConfirmDialog
                close={() => setConfirmDialog('')}
                confirm={onDialogConfim}
                title={t('dialog.deleteDataSet.title')}
                message={t('dialog.deleteDataSet.message')}
                closeBtnText={t('dialog.no')}
                confirmBtnText={t('dialog.yes')}
              />
            )}
            <SumsCriteriaPopup
              close={() => setSumsCriteriaDialog(false)}
              open={sumsCriteriaDialog}
            />
            <MBOSWarningPopup
              close={() => setIsMBOSWarningOpened(false)}
              isOpen={isMBOSWarningOpened}
            />
          </div>
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  campaignSoftware: getCampaignSoftware(state),
  campaignHelp: getCampaignHelp(state),
  originalValues: getOriginalValues(state),
  softwareValidation: getSoftwareValidations(state),
  validationsActive: getCampaignValidationsActive(state)?.all,
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  forms: getSoftwareForms(state),
  campaignInfo: getCampaignInfo(state),
  isVans: getIsVANCR(state),
});

const actionCreators = {
  setSoftware: setCampaignSoftware,
  setOriginValues: setOriginalValues,
  setHelpArea: setAreaHelp,
  setForms: setSoftwareForms,
  setValidations: setSoftWareValidations,
};

export default connect(mapStateToProps, actionCreators)(Software);
